// TriggersBarV3 — footer surface listing active triggers on the source.
//
// Operators DON'T create triggers from this bar (that's done via the
// recipe builder's "Save view + trigger" flow). The bar is a quick
// at-a-glance dashboard: what's running, what's failing, what fired
// last. Click a trigger → settings link to V2 for full edit.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Trigger } from '@datahub/shared';
import { archiveTrigger, patchTrigger } from '../../api';
import { useToast } from '../Toast';

const ACTION_ICON: Record<string, string> = {
  forward_webhook: '→',
  forward_webhook_batched: '⇒',
  promote_to_table: '⤓',
  validate_required: '✓',
  validate_dedupe: '⌱',
  enrich_fetch: '✦',
};

// Operator-readable action labels. Avoid abbreviations that hide
// what the trigger actually does — a fresh operator should be able
// to look at the bar and know which triggers POST somewhere vs which
// land data in BigQuery.
const ACTION_LABEL: Record<string, string> = {
  forward_webhook: 'Forward to webhook',
  forward_webhook_batched: 'Forward in batches',
  promote_to_table: 'Promote to BigQuery table',
  validate_required: 'Require fields',
  validate_dedupe: 'Dedupe by key',
  enrich_fetch: 'Enrich from API',
};

export function TriggersBarV3({ triggers, sourceId, onTriggersChanged }: {
  triggers: Trigger[];
  sourceId: string;
  onTriggersChanged: () => void;
}): React.ReactElement | null {
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const visible = triggers.filter(t => t.status !== 'archived');

  async function togglePause(t: Trigger): Promise<void> {
    setBusyId(t.trigger_id);
    try {
      const next = t.status === 'paused' ? 'active' : 'paused';
      await patchTrigger(sourceId, t.trigger_id, { status: next });
      toast.show(`Trigger ${next === 'paused' ? 'paused' : 'resumed'}.`, { kind: 'success' });
      onTriggersChanged();
    } catch (e) {
      toast.show(`Action failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function deleteTrigger(t: Trigger): Promise<void> {
    const ok = await toast.confirm({
      title: 'Archive this trigger?',
      message: `${t.name ?? '(unnamed)'} (${t.action.type}) will be archived. It will stop firing on new events. Past run history is preserved.`,
      destructive: true,
      confirmLabel: 'Archive trigger',
    });
    if (!ok) return;
    setBusyId(t.trigger_id);
    try {
      await archiveTrigger(sourceId, t.trigger_id);
      toast.show('Trigger archived.', { kind: 'success' });
      onTriggersChanged();
    } catch (e) {
      toast.show(`Archive failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  if (visible.length === 0) {
    return (
      <div style={{
        marginTop: 18, padding: '10px 14px',
        border: '1px dashed var(--line)', borderRadius: 6,
        fontSize: 12, color: 'var(--ink-3)', textAlign: 'center', lineHeight: 1.6,
      }}>
        No automations yet. Build a filter in the data table above, click <strong>+ View</strong>, and pick an action under <em>"Also apply at ingest"</em>. A trigger gets created alongside the saved view — fires on every new event that matches.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 18, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg-elev)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
          Triggers ({visible.length})
        </span>
        <span style={{ flex: 1 }} />
        <Link
          to={`/sources/${sourceId}?v=2`}
          style={{ fontSize: 11, color: 'var(--ink-3)', textDecoration: 'none' }}
          title="Open v2 for full trigger CRUD (rename, version history, run logs, dry-run)"
        >edit in v2 →</Link>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {visible.map(t => {
          const paused = t.status === 'paused';
          const lastFailed = t.last_run_status === 'failure' && !paused;
          const fqIcon = ACTION_ICON[t.action.type] ?? '·';
          const fqLabel = ACTION_LABEL[t.action.type] ?? t.action.type;
          const busy = busyId === t.trigger_id;
          return (
            <div
              key={t.trigger_id}
              title={paused ? 'Paused — will not fire on new events' : lastFailed ? 'Last run: failure' : `Last run: ${t.last_run_status ?? 'pending'}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 4px 4px 10px', fontSize: 11.5,
                background: paused ? 'var(--bg-sunken)' : lastFailed ? 'oklch(0.96 0.06 30 / 0.6)' : 'var(--bg)',
                border: `1px solid ${paused ? 'var(--line)' : lastFailed ? 'oklch(0.78 0.10 30)' : 'var(--line)'}`,
                color: paused ? 'var(--ink-3)' : 'var(--ink)',
                borderRadius: 14,
                opacity: busy ? 0.5 : 1,
              }}
            >
              <span style={{ fontFamily: 'var(--mono)', color: lastFailed ? 'var(--danger)' : 'var(--ink-3)' }}>{fqIcon}</span>
              <strong style={{ fontWeight: 500, textDecoration: paused ? 'line-through' : 'none' }}>{t.name ?? '(unnamed)'}</strong>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-3)' }}>{fqLabel}</span>
              <button
                type="button"
                onClick={() => { void togglePause(t); }}
                disabled={busy}
                title={paused ? 'Resume' : 'Pause'}
                aria-label={paused ? `Resume trigger ${t.name ?? t.trigger_id}` : `Pause trigger ${t.name ?? t.trigger_id}`}
                style={triggerActionBtn}
              ><span aria-hidden="true">{paused ? '▶' : '⏸'}</span></button>
              <button
                type="button"
                onClick={() => { void deleteTrigger(t); }}
                disabled={busy}
                title="Archive trigger"
                aria-label={`Archive trigger ${t.name ?? t.trigger_id}`}
                style={{ ...triggerActionBtn, color: 'var(--danger)' }}
              ><span aria-hidden="true">×</span></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const triggerActionBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
  color: 'var(--ink-3)',
  padding: '2px 6px',
  borderRadius: 4,
  fontFamily: 'var(--mono)',
};
